/**
 * beaconStore — central reactive state for the support Beacon widget.
 *
 * Built on @preact/signals. Mirrors the single-panel, back-nav navigation
 * stack of the real HelpScout Beacon widget it replaces (see beacon.scss
 * for the matching slide transitions).
 *
 * screen values: 'home' | 'ai' | 'guide' | 'guide_article' | 'email' |
 *                'conversations' | 'email_thread'
 * (the AI detail view reuses 'ai' with a stored aiConversationId — there is
 * no separate 'ai_detail' state, see plan "Frontend — Preact komponensfa")
 */

import { signal } from '@preact/signals'

const COMPACT_VIEWPORT_BREAKPOINT = 1200

export function createBeaconStore({
  userId = '',
  tenantId = '',
  personId = '',
  userAvatarUrl = '',
  guideBaseUrl = '',
} = {}) {
  // ── Identity (read-only, passed in from Stimulus values) ────────────────
  const identity = { userId, tenantId, personId, userAvatarUrl, guideBaseUrl }

  // ── Navigation ────────────────────────────────────────────────────────
  const screen       = signal('home')
  const screenStack  = signal([]) // previous screens, for back navigation
  const navDirection = signal('forward') // 'forward' | 'back' — drives the slide transition direction

  // ── Panel open/close ─────────────────────────────────────────────────
  const isOpen = signal(false)

  // ── Viewport ──────────────────────────────────────────────────────────
  const isCompactViewport = signal(
    typeof window !== 'undefined' ? window.innerWidth < COMPACT_VIEWPORT_BREAKPOINT : false
  )

  // ── AI mini-chat ──────────────────────────────────────────────────────
  const aiConversationId = signal(null) // agent_id string | null
  const aiReadOnly       = signal(false) // true when viewing a past conversation from history — no composer, can't be continued

  // ── "Korábbi beszélgetések" list ──────────────────────────────────────
  const conversations        = signal([])
  const conversationsLoading = signal(false)
  const conversationsError   = signal(false)
  const activeEmailThread    = signal(null) // full thread payload for EmailThreadScreen

  // ── Guide search ──────────────────────────────────────────────────────
  const guides        = signal([]) // cached search-index.json entries (survives screen re-mounts)
  const guidesLoading  = signal(false)
  const guidesError    = signal(false)
  const guideQuery     = signal('') // persists the search box text across screen re-mounts (goBack from an article)

  // ── Article reader ────────────────────────────────────────────────────
  const articleSlug  = signal(null)
  const articleTitle = signal(null)
  const panelSize     = signal('compact') // 'compact' | 'expanded'

  // ── Navigation helpers ────────────────────────────────────────────────

  function navigateTo(next) {
    if (next === screen.value) return
    navDirection.value = 'forward'
    screenStack.value = [...screenStack.value, screen.value]
    screen.value = next
  }

  function goBack() {
    const stack = screenStack.value
    navDirection.value = 'back'
    if (!stack.length) {
      screen.value = 'home'
      panelSize.value = 'compact'
      return
    }
    const prev = stack[stack.length - 1]
    screenStack.value = stack.slice(0, -1)
    screen.value = prev
    panelSize.value = 'compact'
  }

  function resetToHome() {
    navDirection.value = 'back'
    screen.value = 'home'
    screenStack.value = []
    panelSize.value = 'compact'
  }

  // ── Panel open/close ──────────────────────────────────────────────────

  function open() {
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
  }

  function toggle() {
    isOpen.value = !isOpen.value
  }

  // ── Viewport ──────────────────────────────────────────────────────────

  function updateViewport() {
    if (typeof window === 'undefined') return
    isCompactViewport.value = window.innerWidth < COMPACT_VIEWPORT_BREAKPOINT
  }

  // ── AI mini-chat ──────────────────────────────────────────────────────

  /** HomeScreen "AI válaszok" card — always starts a brand-new conversation, never continues a previous one. */
  function openAiFromHome() {
    aiConversationId.value = null
    aiReadOnly.value = false
    navigateTo('ai')
  }

  /** ConversationsScreen / HomeScreen history row click — opens a past conversation read-only;
   * it cannot be continued, there is no composer/send button (see plan "Korábbi beszélgetések"). */
  function openAiConversation(agentId) {
    aiConversationId.value = agentId || null
    aiReadOnly.value = true
    navigateTo('ai')
  }

  /** Called by AiScreen once the backend assigns an agent_id to a brand-new conversation, so
   * follow-up messages within the same open AiScreen session attach to it (X-Conversation-Id). */
  function setAiConversationId(agentId) {
    aiConversationId.value = agentId || null
  }

  // ── Conversations list ─────────────────────────────────────────────────

  function setConversations(list) {
    conversations.value = Array.isArray(list) ? list : []
  }

  function openEmailThread(thread) {
    activeEmailThread.value = thread
    navigateTo('email_thread')
  }

  // ── Guide / article reader ─────────────────────────────────────────────

  function setGuides(list) {
    guides.value = Array.isArray(list) ? list : []
  }

  function setGuideQuery(value) {
    guideQuery.value = value || ''
  }

  function openArticle(slug, title) {
    articleSlug.value = slug
    articleTitle.value = title
    panelSize.value = 'compact'
    navigateTo('guide_article')
  }

  function closeArticle() {
    panelSize.value = 'compact'
    goBack()
  }

  function expandPanel() {
    panelSize.value = 'expanded'
  }

  function collapsePanel() {
    panelSize.value = 'compact'
  }

  function togglePanelSize() {
    panelSize.value = panelSize.value === 'expanded' ? 'compact' : 'expanded'
  }

  return {
    identity,

    // Signals
    screen,
    screenStack,
    isOpen,
    isCompactViewport,
    aiConversationId,
    aiReadOnly,
    conversations,
    conversationsLoading,
    conversationsError,
    activeEmailThread,
    guides,
    guidesLoading,
    guidesError,
    guideQuery,
    articleSlug,
    articleTitle,
    panelSize,

    // Navigation
    navigateTo,
    goBack,
    resetToHome,
    navDirection,

    // Open/close
    open,
    close,
    toggle,

    // Viewport
    updateViewport,

    // AI
    openAiFromHome,
    openAiConversation,
    setAiConversationId,

    // Conversations
    setConversations,
    openEmailThread,

    // Guide
    setGuides,
    setGuideQuery,
    openArticle,
    closeArticle,
    expandPanel,
    collapsePanel,
    togglePanelSize,
  }
}
